import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CategoryOutlined as MaterialsIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  HelpOutline as RequestIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import { ORGANIZATION_WRITE_ROLES, PERM_ORGANIZATION_WRITE } from '../auth';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { useOrganizationWasteTypes } from '../hooks/useOrganizationWasteTypes';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType } from '../types/organization.type';
import {
  addOrganizationWasteType,
  removeOrganizationWasteType,
  updateOrganizationWasteType,
  type OrganizationWasteType,
} from '../services/organization.service';
import { createCatalogChangeRequest, getCatalogOptions } from '../services/waste-catalog.service';
import type { CatalogMaterialOption, CatalogOptions } from '../types/waste-catalog.type';
import { getApiErrorDetail } from '../helpers/api-error.helper';

function formatLabel(value?: string | null): string {
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

type RequestForm = { nameEn: string; nameAr: string; description: string; categoryId: string };
const EMPTY_REQUEST: RequestForm = { nameEn: '', nameAr: '', description: '', categoryId: '' };

const Materials: React.FC = () => {
  const { t, i18n } = useTranslation();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { canMutate, orgId } = useAuthPermissions();

  const organizationId = selectedOrganization?.id;
  const orgIsRecycler = selectedOrganization?.organization_type === OrganizationType.RECYCLER;
  const canWrite = orgId ? canMutate(PERM_ORGANIZATION_WRITE(orgId), ORGANIZATION_WRITE_ROLES) : false;

  const {
    rows: wasteTypes,
    isLoading: loading,
    isError: loadFailed,
    invalidate,
  } = useOrganizationWasteTypes(organizationId, orgIsRecycler);

  const [search, setSearch] = useState('');
  const [pageError, setPageError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogOptions | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrganizationWasteType | null>(null);
  const [pickedMaterial, setPickedMaterial] = useState<CatalogMaterialOption | null>(null);
  const [clientCustomId, setClientCustomId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestForm, setRequestForm] = useState<RequestForm>(EMPTY_REQUEST);
  const [requestError, setRequestError] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  useEffect(() => {
    if (!organizationId || !orgIsRecycler) return;
    // Deliberately unscoped: this is the catalog the organization chooses *from*,
    // so it must show everything, not only what it already handles.
    void getCatalogOptions('market', undefined)
      .then(setCatalog)
      .catch(() => setCatalog(null));
  }, [organizationId, orgIsRecycler]);

  const isArabic = i18n.language.startsWith('ar');
  const categoryLabel = (category: { name: string; name_en: string; name_ar: string }) =>
    (isArabic ? category.name_ar : category.name_en) || category.name;
  const materialLabel = (material: CatalogMaterialOption) =>
    (isArabic ? material.name_ar : material.name_en) || material.name;

  const categoryNameByMaterialId = useMemo(() => {
    const map = new Map<string, string>();
    (catalog?.categories ?? []).forEach((category) => {
      (category.materials ?? []).forEach((material) => map.set(material.id, categoryLabel(category)));
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, isArabic]);

  // Only what the organization has not taken yet: adding one it already has is a
  // conflict, so it should never be offered in the first place.
  const availableMaterials = useMemo(() => {
    const held = new Set(wasteTypes.map((row) => row.material_id));
    return (catalog?.categories ?? [])
      .flatMap((category) => category.materials ?? [])
      .filter((material) => !held.has(material.id));
  }, [catalog, wasteTypes]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return wasteTypes;
    return wasteTypes.filter((row) =>
      [row.name, row.code, row.client_custom_id, categoryNameByMaterialId.get(row.material_id)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [wasteTypes, search, categoryNameByMaterialId]);

  const openAdd = () => {
    setEditing(null);
    setPickedMaterial(null);
    setClientCustomId('');
    setSubmitError('');
    setDialogOpen(true);
  };

  const openEdit = (row: OrganizationWasteType) => {
    setEditing(row);
    setPickedMaterial(null);
    setClientCustomId(row.client_custom_id ?? '');
    setSubmitError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditing(null);
    setPickedMaterial(null);
    setSubmitError('');
  };

  const submit = async () => {
    if (!organizationId) return;
    if (!editing && !pickedMaterial) {
      setSubmitError(t('materials.chooseFromCatalog'));
      return;
    }
    setSaving(true);
    setSubmitError('');
    try {
      if (editing) {
        await updateOrganizationWasteType(organizationId, editing.material_id, clientCustomId);
      } else {
        await addOrganizationWasteType(organizationId, pickedMaterial!.id, clientCustomId);
      }
      await invalidate();
      setDialogOpen(false);
      setEditing(null);
      setPickedMaterial(null);
    } catch (error) {
      setSubmitError(getApiErrorDetail(error) ?? t('materials.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row: OrganizationWasteType) => {
    if (!organizationId) return;
    if (!window.confirm(t('materials.removeConfirm', { name: row.name }))) return;
    setPageError(null);
    try {
      await removeOrganizationWasteType(organizationId, row.material_id);
      await invalidate();
    } catch (error) {
      setPageError(getApiErrorDetail(error) ?? t('materials.removeError'));
    }
  };

  const openRequest = () => {
    setRequestForm(EMPTY_REQUEST);
    setRequestError('');
    setRequestOpen(true);
  };

  const submitRequest = async () => {
    if (!organizationId) return;
    if (!requestForm.nameEn.trim() || !requestForm.description.trim()) {
      setRequestError(t('materials.requestInvalid'));
      return;
    }
    setRequestSending(true);
    setRequestError('');
    try {
      await createCatalogChangeRequest({
        organization_id: organizationId,
        requested_name_en: requestForm.nameEn.trim(),
        requested_name_ar: requestForm.nameAr.trim() || undefined,
        description: requestForm.description.trim(),
        suggested_category_id: requestForm.categoryId || undefined,
      });
      setRequestOpen(false);
      setRequestSent(true);
    } catch (error) {
      setRequestError(getApiErrorDetail(error) ?? t('materials.requestError'));
    } finally {
      setRequestSending(false);
    }
  };

  if (!selectedOrganization) {
    return <Alert severity="info">{t('materials.selectOrganization')}</Alert>;
  }

  if (!orgIsRecycler) {
    return <Alert severity="warning">{t('materials.recyclerOnly')}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        icon={<MaterialsIcon />}
        title={t('materials.title')}
        subtitle={t('materials.subtitle')}
        action={
          <Button variant="outlined" startIcon={<RequestIcon />} onClick={openRequest}>
            {t('materials.requestMissing')}
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('materials.intro')}
      </Alert>

      {(pageError || loadFailed) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={pageError ? () => setPageError(null) : undefined}>
          {pageError ?? t('materials.loadError')}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, borderRadius: '8px' }} variant="outlined">
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="center">
          <TextField
            fullWidth
            size="small"
            label={t('materials.search')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ whiteSpace: 'nowrap' }}>
              {t('materials.add')}
            </Button>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('materials.name')}</TableCell>
              <TableCell>{t('materials.category')}</TableCell>
              <TableCell>{t('materials.clientCustomId')}</TableCell>
              <TableCell>{t('materials.status')}</TableCell>
              <TableCell align="right">{t('materials.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {wasteTypes.length === 0 ? t('materials.empty') : t('materials.noMatches')}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((row) => (
                <TableRow key={row.material_id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                  <TableCell>
                    {categoryNameByMaterialId.get(row.material_id) ??
                      t(`categories.${row.parent_category ?? ''}`, formatLabel(row.parent_category))}
                  </TableCell>
                  <TableCell>
                    {row.client_custom_id ? (
                      <Chip size="small" label={row.client_custom_id} variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status === 'active' ? t('materials.statusActive') : t('materials.statusRetired')}
                      color={row.status === 'active' ? 'success' : 'default'}
                      variant={row.status === 'active' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                      {canWrite && (
                        <Tooltip title={t('materials.editTooltip')}>
                          <IconButton size="small" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canWrite && (
                        <Tooltip title={t('materials.removeTooltip')}>
                          <IconButton size="small" color="error" onClick={() => void remove(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editing ? t('materials.editTitle', { name: editing.name }) : t('materials.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editing ? t('materials.editHint') : t('materials.addHint')}
          </Typography>

          {editing ? (
            <TextField fullWidth size="small" label={t('materials.name')} value={editing.name} disabled sx={{ mb: 2 }} />
          ) : (
            <Autocomplete
              options={availableMaterials}
              value={pickedMaterial}
              onChange={(_event, value) => setPickedMaterial(value)}
              getOptionLabel={materialLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              groupBy={(option) => categoryNameByMaterialId.get(option.id) ?? ''}
              noOptionsText={t('materials.noOptionsLeft')}
              renderInput={(params) => (
                <TextField {...params} label={t('materials.name')} size="small" required sx={{ mb: 2 }} />
              )}
            />
          )}

          <TextField
            fullWidth
            size="small"
            label={t('materials.clientCustomId')}
            value={clientCustomId}
            onChange={(event) => setClientCustomId(event.target.value)}
            inputProps={{ maxLength: 64 }}
            helperText={t('materials.clientCustomIdHelp')}
          />

          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={() => void submit()} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={requestOpen} onClose={() => !requestSending && setRequestOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('materials.requestTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('materials.requestIntro')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              required
              label={t('materials.requestNameEn')}
              value={requestForm.nameEn}
              onChange={(event) => setRequestForm((form) => ({ ...form, nameEn: event.target.value }))}
            />
            <TextField
              fullWidth
              size="small"
              label={t('materials.requestNameAr')}
              value={requestForm.nameAr}
              onChange={(event) => setRequestForm((form) => ({ ...form, nameAr: event.target.value }))}
            />
            <TextField
              select
              fullWidth
              size="small"
              label={t('materials.requestCategory')}
              value={requestForm.categoryId}
              onChange={(event) => setRequestForm((form) => ({ ...form, categoryId: event.target.value }))}
            >
              <MenuItem value="">—</MenuItem>
              {(catalog?.categories ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {categoryLabel(category)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              required
              multiline
              minRows={3}
              label={t('materials.requestDescription')}
              value={requestForm.description}
              onChange={(event) => setRequestForm((form) => ({ ...form, description: event.target.value }))}
            />
            {requestError && <Alert severity="error">{requestError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)} disabled={requestSending}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={() => void submitRequest()} disabled={requestSending}>
            {requestSending ? <CircularProgress size={20} /> : t('materials.requestSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={requestSent}
        autoHideDuration={5000}
        onClose={() => setRequestSent(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setRequestSent(false)}>
          {t('materials.requestSent')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Materials;
